import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { User } from '@/api/entities';
import { Plane, Sparkles, Star } from 'lucide-react';

export default function Ad() {
  const [currentUser, setCurrentUser] = useState(null);
  const [destinations, setDestinations] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const navigate = useNavigate();

  useEffect(() => {
    const loadUser = async () => {
      const user = await User.getCurrentUser();
      setCurrentUser(user);
      
      if (user) {
        const roles = user.app_metadata?.roles || [];
        const isAdmin = roles.includes('admin') || user.role === 'admin';
        
        if (!isAdmin) {
          navigate('/');
          return;
        }
        
        // Fetch top destinations
        try {
          const { supabase } = await import('@/lib/supabase');
          const { data: { session } } = await supabase.auth.getSession();
          
          if (session?.access_token) {
            const response = await fetch('/api/admin/top-destinations', {
              headers: {
                'Authorization': `Bearer ${session.access_token}`
              }
            });
            
            if (response.ok) {
              const data = await response.json();
              console.log('Destinations data:', data);
              setDestinations(data.destinations || []);
            } else {
              const errorData = await response.json().catch(() => ({ error: 'Unknown error' }));
              console.error('API error:', errorData);
              setError(errorData.error || 'Failed to fetch destinations');
            }
          } else {
            setError('No session found');
          }
        } catch (error) {
          console.error('Error fetching destinations:', error);
          setError(error.message || 'Failed to fetch destinations');
        } finally {
          setIsLoading(false);
        }
      } else {
        setIsLoading(false);
        navigate('/');
      }
    };

    loadUser();
  }, [navigate]);

  if (isLoading) {
    return (
      <div className="min-h-screen bg-black flex items-center justify-center">
        <div className="text-white">Loading destinations...</div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-black flex items-center justify-center">
        <div className="text-center">
          <div className="text-red-400 mb-2">Error: {error}</div>
          <div className="text-white/60 text-sm">Check console for details</div>
        </div>
      </div>
    );
  }

  // 9:16 aspect ratio container (vertical/portrait for social media)
  // 9:16 means width:height = 9:16 (portrait orientation)
  // Standard Instagram Story size: 1080x1920 (9:16 ratio)
  // Reduced by 60% = 40% of original: 432x768
  const containerWidth = 432;
  const containerHeight = 768; // 432 * (16/9) = 768

  return (
    <>
      <Helmet>
        <title>Defera - Top Destinations</title>
        <meta name="robots" content="noindex, nofollow" />
        <meta name="googlebot" content="noindex, nofollow" />
      </Helmet>
      
      <div className="min-h-screen flex items-center justify-center p-4 relative overflow-hidden">
        {/* Animated 3-color gradient background - Sophisticated and elegant, cycling through color sets */}
        {/* Color Set 1: Dark Red/Orange, Brown, Silver */}
        <div 
          className="absolute inset-0"
          style={{
            background: `
              linear-gradient(45deg, #0a0a0f, #c2410c, #7c2d12, #78716c, #0a0a0f),
              linear-gradient(135deg, #dc2626, #0a0a0f, #ea580c, #a8a29e, #dc2626),
              linear-gradient(225deg, #991b1b, #060A1E, #0a0a0f, #c2410c, #991b1b)
            `,
            backgroundSize: '400% 400%, 350% 350%, 450% 450%',
            animation: 'gradientShift 2.5s ease infinite, colorFade1 16s ease infinite',
            opacity: 1,
            filter: 'blur(40px)',
          }}
        />
        {/* Color Set 2: Deep Burgundy, Copper, Charcoal */}
        <div 
          className="absolute inset-0"
          style={{
            background: `
              linear-gradient(45deg, #0a0a0f, #b91c1c, #78350f, #57534e, #0a0a0f),
              linear-gradient(135deg, #7f1d1d, #0a0a0f, #080E28, #78716c, #7f1d1d),
              linear-gradient(225deg, #991b1b, #854d0e, #0a0a0f, #080E28, #991b1b)
            `,
            backgroundSize: '400% 400%, 350% 350%, 450% 450%',
            animation: 'gradientShift 2.5s ease infinite, colorFade2 16s ease infinite',
            opacity: 0,
            filter: 'blur(40px)',
          }}
        />
        {/* Color Set 3: Rust, Amber, Platinum */}
        <div 
          className="absolute inset-0"
          style={{
            background: `
              linear-gradient(45deg, #0a0a0f, #ea580c, #060A1E, #a8a29e, #0a0a0f),
              linear-gradient(135deg, #c2410c, #0a0a0f, #0A1233, #d6d3d1, #c2410c),
              linear-gradient(225deg, #dc2626, #080E28, #0a0a0f, #2A3868, #dc2626)
            `,
            backgroundSize: '400% 400%, 350% 350%, 450% 450%',
            animation: 'gradientShift 2.5s ease infinite, colorFade3 16s ease infinite',
            opacity: 0,
            filter: 'blur(40px)',
          }}
        />
        {/* Color Set 4: Mahogany, Bronze, Silver */}
        <div 
          className="absolute inset-0"
          style={{
            background: `
              linear-gradient(45deg, #0a0a0f, #991b1b, #7c2d12, #d6d3d1, #0a0a0f),
              linear-gradient(135deg, #b91c1c, #0a0a0f, #060A1E, #a8a29e, #b91c1c),
              linear-gradient(225deg, #7f1d1d, #78350f, #0a0a0f, #c2410c, #7f1d1d)
            `,
            backgroundSize: '400% 400%, 350% 350%, 450% 450%',
            animation: 'gradientShift 2.5s ease infinite, colorFade4 16s ease infinite',
            opacity: 0,
            filter: 'blur(40px)',
          }}
        />
        <style>{`
          @keyframes gradientShift {
            0% {
              background-position: 0% 50%, 100% 0%, 50% 100%;
              transform: scale(1) rotate(0deg);
            }
            25% {
              background-position: 100% 50%, 0% 100%, 0% 0%;
              transform: scale(1.1) rotate(90deg);
            }
            50% {
              background-position: 50% 0%, 50% 50%, 100% 50%;
              transform: scale(1.05) rotate(180deg);
            }
            75% {
              background-position: 0% 100%, 100% 50%, 50% 0%;
              transform: scale(1.08) rotate(270deg);
            }
            100% {
              background-position: 0% 50%, 100% 0%, 50% 100%;
              transform: scale(1) rotate(360deg);
            }
          }
          
          @keyframes colorFade1 {
            0%, 23% { opacity: 1; }
            25%, 48% { opacity: 0; }
            50%, 73% { opacity: 0; }
            75%, 98% { opacity: 0; }
            100% { opacity: 1; }
          }
          
          @keyframes colorFade2 {
            0%, 23% { opacity: 0; }
            25%, 48% { opacity: 1; }
            50%, 73% { opacity: 0; }
            75%, 98% { opacity: 0; }
            100% { opacity: 0; }
          }
          
          @keyframes colorFade3 {
            0%, 23% { opacity: 0; }
            25%, 48% { opacity: 0; }
            50%, 73% { opacity: 1; }
            75%, 98% { opacity: 0; }
            100% { opacity: 0; }
          }
          
          @keyframes colorFade4 {
            0%, 23% { opacity: 0; }
            25%, 48% { opacity: 0; }
            50%, 73% { opacity: 0; }
            75%, 98% { opacity: 1; }
            100% { opacity: 0; }
          }
        `}</style>
        
        <div 
          className="relative overflow-hidden rounded-3xl mx-auto z-10 backdrop-blur-sm"
          style={{
            width: '432px',
            height: '768px',
            aspectRatio: '9 / 16',
            // Force 9:16 vertical orientation (60% reduction = 40% of original)
            maxWidth: '432px',
            maxHeight: '768px',
            backgroundColor: 'rgba(10, 10, 15, 0.3)',
            boxShadow: `
              0 0 40px rgba(255, 255, 255, 0.3),
              0 0 80px rgba(255, 255, 255, 0.2),
              0 0 120px rgba(255, 255, 255, 0.15),
              inset 0 0 60px rgba(255, 255, 255, 0.05)
            `,
            border: '1px solid rgba(255, 255, 255, 0.2)'
          }}
        >
          {/* Background gradient overlay - Semi-transparent */}
          <div className="absolute inset-0 bg-gradient-to-b from-[#0a0a0f] via-[#1a1a2e] to-[#0E1643] opacity-40" />
          
          {/* Animated background pattern */}
          <div className="absolute inset-0 opacity-10">
            <div className="absolute inset-0" style={{
              backgroundImage: `radial-gradient(circle at 20% 50%, rgba(34, 197, 94, 0.1) 0%, transparent 50%),
                                radial-gradient(circle at 80% 80%, rgba(32, 66, 238, 0.1) 0%, transparent 50%)`,
            }} />
          </div>

          {/* Content */}
          <div className="relative z-10 h-full flex flex-col p-8">
            {/* Header with Logo */}
            <div className="flex items-center justify-between mb-6">
              <div className="flex items-center gap-3">
                <img 
                  src="/defera-logo-web.png" 
                  alt="Defera Logo" 
                  className="h-14 object-contain"
                />
              </div>
              <div className="flex items-center gap-2 text-emerald-400">
                <Sparkles className="w-4 h-4" strokeWidth={1.5} />
                <span className="text-xs font-normal tracking-wider" style={{ fontFamily: 'system-ui, -apple-system, sans-serif' }}>Top Searched</span>
              </div>
            </div>

            {/* Main Content */}
            <div className="flex-1 flex flex-col min-h-0">
              <div className="mt-8 mb-8 bg-clip-text" style={{ backgroundImage: 'linear-gradient(to bottom, #9ca3af 0%, #d1d5db 25%, #e5e7eb 50%, #f3f4f6 75%, #ffffff 100%)' }}>
                <h2 className="font-bold mb-0 tracking-tight leading-tight text-transparent" style={{ fontFamily: 'system-ui, -apple-system, sans-serif', fontSize: '2.75rem', lineHeight: '1.1' }}>
                  Top 5
                </h2>
                <h2 className="font-bold mb-0 tracking-tight leading-tight text-transparent" style={{ fontFamily: 'system-ui, -apple-system, sans-serif', fontSize: '2.75rem', lineHeight: '1.1' }}>
                  Destinations
                </h2>
                <h2 className="font-bold tracking-tight leading-tight text-transparent" style={{ fontFamily: 'system-ui, -apple-system, sans-serif', fontSize: '2.75rem', lineHeight: '1.1' }}>
                  Searched
                </h2>
              </div>

              {/* Top 5 Destinations - More compact, positioned lower */}
              <div className="space-y-2 mt-auto mb-4 overflow-y-auto">
                {destinations.length > 0 ? destinations.map((dest, index) => (
                  <div
                    key={dest.city}
                    className="group relative overflow-hidden rounded-3xl bg-gradient-to-r from-white/5 via-white/5 to-transparent backdrop-blur-sm border border-emerald-400/30 px-4 py-[0.8rem] transition-all duration-500 flex-shrink-0"
                  >
                    <div className="flex items-center gap-3">
                      {/* Rank Badge with Number - Star Icon */}
                      <div className="relative flex-shrink-0">
                        <div className="relative flex items-center justify-center transition-transform duration-500" style={{ width: '44px', height: '44px' }}>
                          <Star 
                            className="absolute inset-0 w-full h-full text-emerald-400 fill-emerald-400/30" 
                            strokeWidth={2}
                            style={{ filter: 'drop-shadow(0 0 8px rgba(16, 185, 129, 0.5))' }}
                          />
                          <span className="relative z-10 text-base font-bold text-emerald-200" style={{ fontFamily: 'system-ui, -apple-system, sans-serif' }}>
                            {index + 1}
                          </span>
                        </div>
                        <div className="absolute -inset-1 bg-gradient-to-br from-emerald-400/20 to-emerald-600/20 rounded-full blur-md opacity-80 -z-10 transition-opacity duration-500"></div>
                      </div>
                      
                      {/* City Name with Bold Typography - Smaller */}
                      <div className="flex-1 min-w-0">
                        <h3 
                          className="text-xl font-bold text-emerald-100 tracking-tight transition-colors duration-500"
                          style={{ fontFamily: 'system-ui, -apple-system, sans-serif' }}
                        >
                          {dest.city}
                        </h3>
                      </div>
                      
                      {/* Decorative Plane Icon - Smaller */}
                      <div className="flex-shrink-0 opacity-100 transition-all duration-500">
                        <div className="w-10 h-10 rounded-full bg-gradient-to-br from-emerald-400/20 via-emerald-500/10 to-emerald-600/20 border-2 border-emerald-400/30 flex items-center justify-center backdrop-blur-sm">
                          <Plane className="w-5 h-5 text-emerald-400" strokeWidth={2} />
                        </div>
                      </div>
                    </div>
                    
                    {/* Bottom accent line - Always visible */}
                    <div className="absolute bottom-0 left-0 right-0 h-0.5 bg-gradient-to-r from-transparent via-emerald-400/40 to-transparent opacity-100 transition-opacity duration-500" />
                  </div>
                )) : (
                  <div className="text-center py-12">
                    <p className="text-white/60 text-lg">No destination data available yet</p>
                    <p className="text-white/40 text-sm mt-2">Cart searches will appear here</p>
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* Decorative elements */}
          <div className="absolute top-0 right-0 w-64 h-64 bg-emerald-400/5 rounded-full blur-3xl -translate-y-1/2 translate-x-1/2" />
          <div className="absolute bottom-0 left-0 w-96 h-96 bg-brand-400/5 rounded-full blur-3xl translate-y-1/2 -translate-x-1/2" />
        </div>
      </div>
    </>
  );
}
